// src/shared/components/SessionListener.js
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getToken, isTokenExpired, removeToken } from 'lib/auth/authUtils';
import { showToast } from 'app/notificationSlice';

const SessionListener = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Session check function
    const checkSession = () => {
      // Skip check on login page
      if (window.location.pathname === '/login') return;

      const token = getToken();

      // Case 1: No token exists at all
      if (!token) {
        dispatch(showToast({ message: 'You are not logged in. Redirecting to login.', severity: 'warning' }));
        navigate('/login', { replace: true });
        return;
      }

      // Case 2: Token exists but has expired
      if (isTokenExpired()) {
        removeToken();
        dispatch(showToast({ message: 'Your session has expired. Please log in again.', severity: 'warning' }));
        navigate('/login', { replace: true });
      }
    };

    // Create a debounced function to avoid too many checks
    let timeoutId;
    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkSession, 500);
    };

    // Set periodic session check (every 1 minute)
    const intervalId = setInterval(checkSession, 60000);
    
    // Check on specific user actions instead of all clicks
    document.addEventListener('mousedown', debouncedCheck);
    document.addEventListener('keydown', debouncedCheck);
    window.addEventListener('focus', checkSession);
    
    // Clean up
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', debouncedCheck);
      document.removeEventListener('keydown', debouncedCheck);
      window.removeEventListener('focus', checkSession);
    };
  }, [navigate, dispatch]);

  // This component doesn't render anything
  return null;
};

export default SessionListener;